import React, { useState, useEffect } from 'react';
import { NavLink, Collapse, Box, TextInput } from '@mantine/core';
import { IconChevronDown, IconChevronRight, IconSearch } from '@tabler/icons-react';
import * as TablerIcons from '@tabler/icons-react';

export default function DynamicMenu({ 
  onSelectModule, 
  currentPath, 
  openMenus, 
  setOpenMenus,
  openSubMenus,
  setOpenSubMenus 
}) {
  const [menuData, setMenuData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentCyExists, setCurrentCyExists] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Chargement des données de menu et vérification CURRENT_CY
  useEffect(() => {
    const loadMenuData = async () => {
      try {
        // Charger les données de menu
        const menuResponse = await fetch('/api/menu_items');
        const menuData = await menuResponse.json();
        
        // Vérifier l'existence de CURRENT_CY
        let cyExists = false;
        try {
          const cyResponse = await fetch('/api/parametres/system/current-cy');
          const cyData = await cyResponse.json();
          
          // CURRENT_CY existe si la réponse est successful ET contient une année valide
          // OU si le status HTTP est 200 (même si success peut être false)
          cyExists = cyResponse.ok && cyData.success && cyData.data && cyData.data.year;
        } catch (error) {
          // En cas d'erreur réseau, considérer que CURRENT_CY n'existe pas
          cyExists = false;
        }
        
        if (menuData.success) {
          setMenuData(menuData.data);
        } else {
          console.error('Erreur lors du chargement du menu:', menuData.message);
        }
        
        setCurrentCyExists(cyExists);
      } catch (error) {
        console.error('Erreur lors du chargement du menu:', error);
      } finally {
        setLoading(false);
      }
    };

    loadMenuData();
  }, []);

  // Fonction pour obtenir l'icône Tabler avec couleur conditionnelle
  const getIcon = (iconName, isActive = false) => {
    if (!iconName) return null;
    const IconComponent = TablerIcons[iconName];
    return IconComponent ? (
      <IconComponent 
        size={16} 
        style={{ 
          color: isActive ? '#40c057' : undefined,
          transition: 'color 0.2s ease'
        }} 
      />
    ) : null;
  };

  // Fonction pour basculer l'état d'un menu
  const toggleMenu = (menuId) => {
    setOpenMenus(prev => {
      const newState = { ...prev };
      
      // Fermer tous les autres menus du même niveau
      Object.keys(newState).forEach(key => {
        if (key.startsWith('menu_') && key !== `menu_${menuId}`) {
          newState[key] = false;
        }
      });
      
      // Basculer le menu cliqué
      newState[`menu_${menuId}`] = !prev[`menu_${menuId}`];
      return newState;
    });
    
    // Fermer tous les sous-menus
    setOpenSubMenus({});
  };

  // Fonction pour basculer l'état d'un sous-menu
  const toggleSubMenu = (subMenuId) => {
    setOpenSubMenus(prev => {
      const newState = { ...prev };
      
      // Fermer tous les autres sous-menus du même niveau
      Object.keys(newState).forEach(key => {
        if (key.startsWith('submenu_') && key !== `submenu_${subMenuId}`) {
          newState[key] = false;
        }
      });
      
      // Basculer le sous-menu cliqué
      newState[`submenu_${subMenuId}`] = !prev[`submenu_${subMenuId}`];
      return newState;
    });
  };

  // Fonction pour filtrer récursivement les éléments du menu
  const filterMenuItems = (items, query) => {
    if (!query.trim()) return items;
    
    const filtered = [];
    
    for (const item of items) {
      // Vérifier si l'élément actuel correspond
      const matchesItem = item.label.toLowerCase().includes(query.toLowerCase()) ||
                         (item.route && item.route.toLowerCase().includes(query.toLowerCase()));
      
      // Filtrer récursivement les enfants
      const filteredChildren = item.children ? filterMenuItems(item.children, query) : [];
      
      // Inclure l'élément si lui-même ou ses enfants correspondent
      if (matchesItem || filteredChildren.length > 0) {
        filtered.push({
          ...item,
          children: filteredChildren
        });
      }
    }
    
    return filtered;
  };

  // Obtenir les éléments de menu filtrés (mémorisé pour éviter les recalculs)
  const filteredMenuData = React.useMemo(() => {
    return filterMenuItems(menuData, searchQuery);
  }, [menuData, searchQuery]);



  // Rendu récursif du menu
  const renderMenuItem = (item, level = 0) => {
    const hasChildren = item.children && item.children.length > 0;
    const isOpen = level === 0 
      ? openMenus[`menu_${item.id}`] 
      : openSubMenus[`submenu_${item.id}`];

    // Vérifier si cet élément doit être désactivé
    const isDisabled = (item.route === '/parametrage/annuels/parametrage-cy' || 
                       item.route === '/parametrage/annuels/objectifs') && !currentCyExists;

    if (hasChildren) {
      return (
        <React.Fragment key={item.id}>
          <NavLink
            label={item.label}
            leftSection={getIcon(item.icon, false)}
            rightSection={
              isOpen ? <IconChevronDown size={level === 0 ? 16 : 14} /> : <IconChevronRight size={level === 0 ? 16 : 14} />
            }
            onClick={() => level === 0 ? toggleMenu(item.id) : toggleSubMenu(item.id)}
          />
          <Collapse in={isOpen}>
            <Box pl={level === 0 ? "md" : "sm"}>
              {item.children.map(child => renderMenuItem(child, level + 1))}
            </Box>
          </Collapse>
        </React.Fragment>
      );
    } else {
      // Élément de menu final (avec route)
      return (
        <NavLink
          key={item.id}
          label={item.label}
          leftSection={getIcon(item.icon, currentPath === item.route)}
          onClick={() => !isDisabled && item.route && onSelectModule(item.route)}
          active={currentPath === item.route}
          disabled={isDisabled}
          style={isDisabled ? { 
            opacity: 0.5, 
            cursor: 'not-allowed',
            color: '#868e96'
          } : currentPath === item.route ? {
            backgroundColor: '#e6fcf0',
            borderLeft: '3px solid #40c057',
            color: '#2b8a3e'
          } : {}}
        />
      );
    }
  };

  if (loading) {
    return <div>Chargement du menu...</div>;
  }

  return (
    <>
      {/* Champ de recherche */}
      <Box mb="md" px="sm">
        <TextInput
          placeholder="Rechercher..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.currentTarget.value)}
          leftSection={<IconSearch size={16} />}
          size="sm"
          styles={{
            input: {
              fontSize: '13px'
            }
          }}
        />
      </Box>
      
      {/* Menu filtré */}
      {filteredMenuData.length === 0 && searchQuery.trim() ? (
        <Box px="sm" py="xs" style={{ color: '#868e96', fontSize: '13px', fontStyle: 'italic' }}>
          Aucun résultat trouvé
        </Box>
      ) : (
        filteredMenuData.map(item => renderMenuItem(item))
      )}
    </>
  );
} 